import React, { useContext } from 'react';
import { createPortal } from 'react-dom';
import { MdClose, MdCheckCircle } from 'react-icons/md';
import { LanguageContext } from '../../context'; 

import PaywallHeroImage from '../../assets/images/paywall.png';
import QrCodeIos from '../../assets/images/qr-ios.png'; 
import QrCodeAndroid from '../../assets/images/qr-android.png'; 

interface Props {
  isVisible: boolean;
  onClose: () => void;
}

const PaywallDialog: React.FC<Props> = ({ isVisible, onClose }) => {
  const lang = useContext(LanguageContext) as { t?: (key: string) => string } | null;
  const t = lang && typeof lang.t === 'function' ? lang.t : (key: string) => key;

  if (!isVisible) return null;

  const features = [
    t('paywall.feature_1'),
    t('paywall.feature_2'),
    t('paywall.feature_3'),
    t('paywall.feature_4'),
    t('paywall.feature_5')
  ];

  // The navbar (MUI AppBar, z-index 1100) must not cover the close button, so the dialog is
  // rendered outside the page's stacking contexts, above the navbar, drawer and MUI modals.
  return createPortal(
    <div style={styles.overlay} onClick={onClose}>
      <div 
        style={styles.container} 
        onClick={(e) => e.stopPropagation()} 
      >
        <button style={styles.closeIcon} onClick={onClose} aria-label="Close">
          <MdClose size={28} color="#000" />
        </button>

        <div style={styles.scrollContent}>
          <div style={styles.imageContainer}>
            <img src={PaywallHeroImage} style={styles.image} alt="Paywall Hero" />
          </div>

          <div style={styles.textContent}>
            <h2 style={styles.titleText}>{t('paywall.title')}</h2>
            <p style={styles.subtitleText}>{t('paywall.subtitle')}</p>

            <div style={styles.featuresContainer}>
              {features.map((feature, index) => (
                <div key={index} style={styles.featureRow}>
                  <MdCheckCircle size={22} color="#10b981" style={styles.featureIcon} />
                  <span style={styles.featureText}>{feature}</span>
                </div>
              ))}
            </div>

            <div style={styles.pcNoticeContainer}>
              <p style={styles.pcNoticeText}>
                {t('paywall.pc_notice')}
              </p>
            </div>

            <div style={styles.storeLinksContainer}>
              <div style={styles.storeBlock}>
                <p style={styles.storeLabel}>{t('paywall.ios_label')}</p>
                <img src={QrCodeIos} alt="iOS QR Code" style={styles.qrCode} />
                <a 
                  href="https://apps.apple.com/jp/app/chrono-spark-%E3%82%AF%E3%83%AD%E3%83%8E%E3%82%B9%E3%83%91%E3%83%BC%E3%82%AF/id6760439535" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  style={styles.storeLink}
                >
                  {t('paywall.ios_link')}
                </a>
              </div>

              <div style={styles.storeBlock}>
                <p style={styles.storeLabel}>{t('paywall.android_label')}</p>
                <img src={QrCodeAndroid} alt="Android QR Code" style={styles.qrCode} />
                <a 
                  href="https://play.google.com/store/apps/details?id=jp.chronospark.chronosparkMobile" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  style={styles.storeLink}
                >
                  {t('paywall.android_link')}
                </a>
              </div>
            </div>

            {/* 法的リンク3種（利用規約、プライバシーポリシー、EULA） */}
            <div style={styles.legalLinksContainer}>
              <a 
                href="https://chronospark.jp/terms/" 
                target="_blank" 
                rel="noopener noreferrer" 
                style={styles.legalSubText}
              >
                {t('paywall.terms_of_service')}
              </a>
              
              <span style={styles.legalSeparator}>・</span>

              <a 
                href="https://chronospark.jp/privacy/" 
                target="_blank" 
                rel="noopener noreferrer" 
                style={styles.legalSubText}
              >
                {t('paywall.privacy_policy')}
              </a>
              
              <span style={styles.legalSeparator}>・</span>
              
              <a 
                href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" 
                target="_blank" 
                rel="noopener noreferrer" 
                style={styles.legalSubText}
              >
                {t('paywall.eula')}
              </a>
            </div>

          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2000 },
  container: { backgroundColor: 'white', borderRadius: '20px', overflow: 'hidden', width: '90%', maxWidth: '500px', maxHeight: '90vh', position: 'relative', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' },
  scrollContent: { overflowY: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingBottom: '30px' },
  closeIcon: { position: 'absolute', top: '15px', right: '15px', zIndex: 20, backgroundColor: 'rgba(255,255,255,0.8)', borderRadius: '50%', padding: '4px', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  imageContainer: { width: '100%', height: '180px', marginBottom: '20px' },
  image: { width: '100%', height: '100%', objectFit: 'cover' },
  textContent: { padding: '0 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', boxSizing: 'border-box' },
  titleText: { fontSize: '22px', fontWeight: 'bold', color: '#000', textAlign: 'center', margin: '0 0 8px 0' },
  subtitleText: { fontSize: '13px', color: '#666', textAlign: 'center', margin: '0 0 20px 0', lineHeight: '1.4' },
  featuresContainer: { width: '100%', marginBottom: '25px', padding: '0 10px', boxSizing: 'border-box' },
  featureRow: { display: 'flex', alignItems: 'center', marginBottom: '12px' },
  featureIcon: { marginRight: '10px', flexShrink: 0 },
  featureText: { fontSize: '14px', color: '#333', fontWeight: 500 },
  pcNoticeContainer: { backgroundColor: '#FFF4E5', border: '1px solid #FFB020', borderRadius: '8px', padding: '15px', width: '100%', boxSizing: 'border-box', marginBottom: '25px' },
  pcNoticeText: { color: '#B76E00', fontSize: '15px', fontWeight: 'bold', textAlign: 'center', margin: 0, lineHeight: '1.5' },
  storeLinksContainer: { display: 'flex', flexDirection: 'row', justifyContent: 'center', gap: '30px', width: '100%', flexWrap: 'wrap', marginBottom: '25px' },
  storeBlock: { display: 'flex', flexDirection: 'column', alignItems: 'center' },
  storeLabel: { fontSize: '14px', fontWeight: 'bold', color: '#333', margin: '0 0 10px 0' },
  qrCode: { width: '100px', height: '100px', marginBottom: '10px', border: '1px solid #eee', borderRadius: '8px' },
  storeLink: { fontSize: '12px', color: '#1A73E8', textDecoration: 'none', fontWeight: 'bold' },
  legalLinksContainer: { display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', marginTop: '10px' },
  legalSubText: { color: '#999', fontSize: '11px', textDecoration: 'underline', cursor: 'pointer' },
  legalSeparator: { color: '#ccc', fontSize: '11px', margin: '0 8px' },
};

export default PaywallDialog;